import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, getSupabaseAdmin } from 'src/supabaseClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAdmin: boolean;
  hasAdminAccess: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user has admin privileges
  const isAdmin = user?.app_metadata?.role === 'admin' || 
                  user?.app_metadata?.roles?.includes('admin') ||
                  user?.user_metadata?.role === 'admin';

  // For user management, we need either admin role or service key access
  const hasAdminAccess = isAdmin || !!process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;

  useEffect(() => {
    // Check if this was a temporary session that should be cleared
    const isTempSession = sessionStorage.getItem('supabase.temp.session');
    
    // Get initial session
    const getSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
      } else {
        // If it's a temp session and we're in a new browser session, clear it
        if (isTempSession && session) {
          // Check if this is a fresh browser session by checking sessionStorage
          const sessionStart = sessionStorage.getItem('supabase.session.start');
          if (!sessionStart) {
            // This is a new browser session, clear the auth
            await supabase.auth.signOut();
            sessionStorage.removeItem('supabase.temp.session');
            setSession(null);
            setUser(null);
          } else {
            setSession(session);
            setUser(session?.user ?? null);
          }
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
      }
      setLoading(false);
    };

    // Mark this session as started
    if (isTempSession) {
      sessionStorage.setItem('supabase.session.start', 'true');
    }

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Handle session persistence based on rememberMe
    if (!error && !rememberMe) {
      // Store a flag to clear session on browser close
      sessionStorage.setItem('supabase.temp.session', 'true');
      
      // Set up an event listener to clear the session when the browser closes
      const handleBeforeUnload = () => {
        if (sessionStorage.getItem('supabase.temp.session')) {
          supabase.auth.signOut();
        }
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      // Remove the temporary session flag if remembering
      sessionStorage.removeItem('supabase.temp.session');
    }

    return { error };
  };

  const signOut = async () => {
    // Clean up remember me related storage
    sessionStorage.removeItem('supabase.temp.session');
    sessionStorage.removeItem('supabase.session.start');
    
    await supabase.auth.signOut();
  };

  const refreshUser = async () => {
    try {
      console.log('🔄 RefreshUser called');
      
      // Try to get fresh user data from admin client if available and we have a user
      const adminClient = getSupabaseAdmin();
      if (user && adminClient) {
        console.log('🔧 Using admin client to refresh user data');
        const { data: adminUserData, error: adminError } = await adminClient.auth.admin.getUserById(user.id);
        
        if (!adminError && adminUserData.user) {
          console.log('✅ RefreshUser success (admin):', {
            userId: adminUserData.user.id,
            email: adminUserData.user.email,
            app_metadata: adminUserData.user.app_metadata,
            avatar_url: adminUserData.user.app_metadata?.avatar_url
          });
          setUser(adminUserData.user);
          return;
        } else {
          console.log('⚠️ Admin refresh failed, falling back to regular getUser:', adminError);
        }
      }
      
      // Fallback to regular getUser
      const { data: { user: userData }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('❌ Error refreshing user:', error);
      } else {
        console.log('✅ RefreshUser success (regular):', {
          userId: userData?.id,
          email: userData?.email,
          app_metadata: userData?.app_metadata,
          avatar_url: userData?.app_metadata?.avatar_url
        });
        setUser(userData);
      }
    } catch (error) {
      console.error('❌ Error refreshing user:', error);
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signOut,
    refreshUser,
    isAdmin,
    hasAdminAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
